package com.zaurh.portfolio.main

import androidx.compose.foundation.Image
import androidx.compose.foundation.background
import androidx.compose.foundation.border
import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxSize
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.safeContentPadding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.foundation.lazy.grid.GridCells
import androidx.compose.foundation.lazy.grid.LazyVerticalGrid
import androidx.compose.foundation.rememberScrollState
import androidx.compose.foundation.verticalScroll
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.IconButton
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Switch
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.derivedStateOf
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateMapOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.input.pointer.PointerIcon
import androidx.compose.ui.input.pointer.pointerHoverIcon
import androidx.compose.ui.layout.ContentScale
import androidx.compose.ui.layout.onGloballyPositioned
import androidx.compose.ui.layout.positionInParent
import androidx.compose.ui.platform.LocalDensity
import androidx.compose.ui.text.SpanStyle
import androidx.compose.ui.text.buildAnnotatedString
import androidx.compose.ui.text.font.FontStyle
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.text.withStyle
import androidx.compose.ui.unit.dp
import androidx.compose.ui.unit.sp
import com.zaurh.portfolio.data.gradients
import com.zaurh.portfolio.domain.repository.ContactRepository
import com.zaurh.portfolio.main.component.ContactInfo
import com.zaurh.portfolio.utils.getPoppinsFont
import kotlinx.browser.window
import kotlinx.coroutines.launch
import org.jetbrains.compose.resources.painterResource
import org.koin.compose.viewmodel.koinViewModel
import portfolio.shared.generated.resources.Res
import portfolio.shared.generated.resources.app_store_logo
import portfolio.shared.generated.resources.bober_template
import portfolio.shared.generated.resources.coinway_template
import portfolio.shared.generated.resources.compose_multiplatform
import portfolio.shared.generated.resources.cotam_template
import portfolio.shared.generated.resources.durak_template
import portfolio.shared.generated.resources.github_logo
import portfolio.shared.generated.resources.github_logo_org
import portfolio.shared.generated.resources.google_play_logo
import portfolio.shared.generated.resources.google_play_logo_org
import portfolio.shared.generated.resources.guessy_template
import portfolio.shared.generated.resources.ic_moon
import portfolio.shared.generated.resources.ic_sun
import portfolio.shared.generated.resources.linkedin_logo
import portfolio.shared.generated.resources.linkedin_logo_org
import portfolio.shared.generated.resources.logo_bober
import portfolio.shared.generated.resources.logo_coinway
import portfolio.shared.generated.resources.logo_cotam
import portfolio.shared.generated.resources.logo_durak
import portfolio.shared.generated.resources.logo_github
import portfolio.shared.generated.resources.logo_guessy
import portfolio.shared.generated.resources.logo_linkedin
import portfolio.shared.generated.resources.logo_mapquiz
import portfolio.shared.generated.resources.logo_movietime
import portfolio.shared.generated.resources.logo_notefire
import portfolio.shared.generated.resources.logo_notefire_cloud
import portfolio.shared.generated.resources.logo_playstore
import portfolio.shared.generated.resources.logo_polskismak
import portfolio.shared.generated.resources.logo_postgram
import portfolio.shared.generated.resources.logo_sunnyday
import portfolio.shared.generated.resources.logo_zaureats
import portfolio.shared.generated.resources.mapquiz_template
import portfolio.shared.generated.resources.movietime_template
import portfolio.shared.generated.resources.notefire_cloud_template
import portfolio.shared.generated.resources.notefire_template
import portfolio.shared.generated.resources.polskismak_template
import portfolio.shared.generated.resources.postgram_template
import portfolio.shared.generated.resources.sunnyday_template
import portfolio.shared.generated.resources.zaureats_template
import kotlin.math.log
import kotlin.math.max
import kotlin.math.roundToInt

@Composable
fun MainScreen(
    viewModel: MainViewModel
) {
    val darkMode = viewModel.darkModeEnabled.collectAsState()
    BoxWithConstraints {
        val maxWidth = maxWidth
        val isMobile = maxWidth < 600.dp

        val density = LocalDensity.current
        val coroutineScope = rememberCoroutineScope()

        val scrollState = rememberScrollState()

        val sectionOffsets = remember { mutableStateMapOf<String, Int>() }

        val sections = listOf("Home", "About", "Projects", "Contact")

        val headerOffsetPx = with(density) { 80.dp.toPx() }.toInt()

        val currentSection by remember {
            derivedStateOf {
                val scrollY = scrollState.value

                if (scrollY >= scrollState.maxValue - 20) {
                    "Contact"
                } else {
                    sections
                        .mapNotNull { name -> sectionOffsets[name]?.let { name to it } }
                        .sortedBy { it.second }
                        .lastOrNull { (_, offset) ->
                            scrollY + headerOffsetPx >= offset
                        }
                        ?.first ?: "Home"
                }
            }
        }

        val firstSpacer = (maxWidth * 0.01f)
            .coerceIn(0.dp, 84.dp)

        val shuffledGradients = remember {
            gradients.shuffled()
        }

        val arrangement = if (isMobile) Arrangement.SpaceAround else
            Arrangement.spacedBy(
                36.dp,
                alignment = Alignment.CenterHorizontally
            )

        val headerTextSize = (maxWidth.value * 0.02f)
            .coerceIn(12f, 16f)
            .sp

        Column(
            modifier = Modifier
                .background(MaterialTheme.colorScheme.surface)
                .safeContentPadding()
                .fillMaxSize(),
            horizontalAlignment = Alignment.CenterHorizontally
        ) {
            Spacer(modifier = Modifier.height(16.dp))
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(bottom = 12.dp),
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(
                    modifier = Modifier.weight(1f),
                    horizontalArrangement = arrangement
                ) {
                    Row(
                        horizontalArrangement = Arrangement.spacedBy(36.dp)
                    ) {
                        sections.forEach { name ->
                            HeaderText(
                                title = name,
                                textUnit = headerTextSize,
                                isSelected = currentSection == name
                            ) {
                                sectionOffsets[name]?.let { target ->
                                    coroutineScope.launch {
                                        scrollState.animateScrollTo(target)
                                    }
                                }
                            }
                        }
                    }
                }

                val darkModeSize = (maxWidth * 0.05f).coerceIn(12.dp, 36.dp)

                Icon(
                    painter = painterResource(if (darkMode.value) Res.drawable.ic_sun else Res.drawable.ic_moon),
                    contentDescription = null,
                    modifier = Modifier.padding(end = 24.dp).size(darkModeSize).clickable {
                        viewModel.toggleDarkMode()
                    },
                    tint = if (darkMode.value) Color.White else MaterialTheme.colorScheme.primary
                )
            }

            HorizontalDivider(
                thickness = 1.dp,
                color = MaterialTheme.colorScheme.outline.copy(alpha = 0.2f)
            )

            Column(
                modifier = Modifier
                    .fillMaxSize()
                    .verticalScroll(scrollState),
                horizontalAlignment = Alignment.CenterHorizontally
            ) {
                Spacer(modifier = Modifier.height(firstSpacer))

                val imageSize = (maxWidth * 0.1f).coerceIn(380.dp, 400.dp)
                val socialMediaSize = (maxWidth * 0.09f).coerceIn(24.dp, 64.dp)

                val titleSize = (maxWidth.value * 0.06f)
                    .coerceIn(20f, 64f)
                    .sp

                val subtitleSize = (maxWidth.value * 0.03f)
                    .coerceIn(16f, 36f)
                    .sp

                Box(
                    modifier = Modifier
                        .fillMaxSize()
                        .padding(horizontal = 48.dp)
                        .onGloballyPositioned { coords ->
                            sectionOffsets["Home"] = coords.positionInParent().y.roundToInt()
                        }
                ) {
                    Row(
                        modifier = Modifier.fillMaxWidth(),
                        horizontalArrangement = Arrangement.SpaceAround,
                        verticalAlignment = Alignment.CenterVertically
                    ) {
                        Column {
                            Text(
                                text = "Hello, I'm Zaur",
                                color = MaterialTheme.colorScheme.primary,
                                fontSize = titleSize,
                                lineHeight = titleSize,
                                fontFamily = getPoppinsFont()
                            )

                            Text(
                                text = "Software Developer",
                                fontStyle = FontStyle.Italic,
                                color = MaterialTheme.colorScheme.tertiary,
                                fontSize = subtitleSize,
                                lineHeight = subtitleSize,
                                fontFamily = getPoppinsFont()
                            )

                            Spacer(modifier = Modifier.height(12.dp))

                            Row(
                                verticalAlignment = Alignment.CenterVertically,
                                horizontalArrangement = Arrangement.spacedBy(14.dp)
                            ) {
                                SocialMediaItem(
                                    logo = Res.drawable.logo_linkedin,
                                    hoveredLogo = Res.drawable.linkedin_logo_org,
                                    size = socialMediaSize
                                ) {
                                    window.open("https://linkedin.com/in/zaurh")
                                }

                                SocialMediaItem(
                                    logo = Res.drawable.github_logo,
                                    hoveredLogo = Res.drawable.github_logo,
                                    size = socialMediaSize
                                ) {
                                    window.open("https://github.com/zaurh")
                                }

                                SocialMediaItem(
                                    logo = Res.drawable.logo_playstore,
                                    hoveredLogo = Res.drawable.google_play_logo_org,
                                    size = socialMediaSize
                                ) {
                                    window.open("https://play.google.com/store/apps/dev?id=8494981660664456351")
                                }

                                SocialMediaItem(
                                    logo = Res.drawable.app_store_logo,
                                    hoveredLogo = Res.drawable.app_store_logo,
                                    size = socialMediaSize
                                ) {
                                    window.open("https://apps.apple.com/us/developer/zaur-hasanov/id6806218069")
                                }
                            }
                            Spacer(modifier = Modifier.height(12.dp))
                            CvButton()
                        }

                        Image(
                            painter = painterResource(Res.drawable.compose_multiplatform),
                            contentDescription = null,
                            modifier = Modifier.size(imageSize).align(Alignment.Top),
                            contentScale = ContentScale.Fit
                        )
                    }
                }


                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .onGloballyPositioned { coords ->
                            sectionOffsets["About"] = coords.positionInParent().y.roundToInt()
                        }
                        .padding(vertical = 64.dp),
                    contentAlignment = Alignment.Center
                ) {
                    Text(
                        text = buildAnnotatedString {
                            append("I'm a Software Developer with ")

                            withStyle(
                                SpanStyle(
                                    color = MaterialTheme.colorScheme.tertiary,
                                    fontSize = 18.sp,
                                    fontFamily = getPoppinsFont()
                                )
                            ) {
                                append("4 years")
                            }

                            append(" of experience in Android development, including\n ")

                            withStyle(
                                SpanStyle(
                                    color = MaterialTheme.colorScheme.tertiary,
                                    fontSize = 18.sp,
                                    fontFamily = getPoppinsFont()
                                )
                            ) {
                                append("2 years")
                            }

                            append(" of experience with Kotlin Multiplatform and ")

                            withStyle(
                                SpanStyle(
                                    color = MaterialTheme.colorScheme.tertiary,
                                    fontSize = 18.sp,
                                    fontFamily = getPoppinsFont()
                                )
                            ) {
                                append("KTOR")
                            }

                            append(" backend development.\n")
                            append("I've been working professionally as an Android Developer for nearly 2 years,\n")
                            append("and this portfolio includes some of the projects I've built over the years.\n")
                            append("The website itself is built with ")

                            withStyle(
                                SpanStyle(
                                    color = MaterialTheme.colorScheme.tertiary,
                                    fontSize = 18.sp,
                                    fontFamily = getPoppinsFont()
                                )
                            ) {
                                append("Kotlin Multiplatform")
                            }

                            append(".")
                        },
                        fontFamily = getPoppinsFont(),
                        color = MaterialTheme.colorScheme.primary,
                        textAlign = TextAlign.Center
                    )
                }


//                Box(
//                    modifier = Modifier
//                        .fillMaxWidth()
//                        .onGloballyPositioned { coords ->
//                            sectionOffsets["Skills"] = coords.positionInParent().y.roundToInt()
//                        }
//                ) {
//                    Text("")
//                }

                FlowRow(
                    Modifier.fillMaxWidth().onGloballyPositioned { coords ->
                        sectionOffsets["Projects"] = coords.positionInParent().y.roundToInt()
                    },
                    horizontalArrangement = Arrangement.SpaceAround,
                    maxItemsInEachRow = 2
                ) {
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.mapquiz_template,
                        title = "MapQuiz",
                        desc = "Learn geography while having fun with interactive map quizzes",
                        logo = Res.drawable.logo_mapquiz,
                        colorFirst = shuffledGradients[0].first,
                        colorSecond = shuffledGradients[0].second,
                        showGithub = false,
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.mapquiz")
                        },
                        showAppStore = true,
                        onAppStoreClick = {
                            window.open("https://apps.apple.com/us/app/mapquiz-geography-game/id6806218066")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.movietime_template,
                        title = "MovieTime",
                        desc = "Browse popular movies, watch trailers, and keep up with the latest releases",
                        logo = Res.drawable.logo_movietime,
                        logoBackground = Color(0XFFFFAC39),
                        colorFirst = shuffledGradients[1].first,
                        colorSecond = shuffledGradients[1].second,
                        showGithub = false,
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.movieappintern2")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.bober_template,
                        title = "Bober",
                        desc = "Dating application where you can meet new people and chat with them in real time",
                        logo = Res.drawable.logo_bober,
                        colorFirst = shuffledGradients[2].first,
                        colorSecond = shuffledGradients[2].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/Bober")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.bober")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.polskismak_template,
                        title = "Polski Smak",
                        desc = "Recipe app where you can find the recipes of popular Poland dishes",
                        logo = Res.drawable.logo_polskismak,
                        logoBackground = Color(0xFFF0BA5B),
                        colorFirst = shuffledGradients[3].first,
                        colorSecond = shuffledGradients[3].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/polskismak")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.polskismak")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.cotam_template,
                        title = "Co Tam",
                        desc = "Chat instantly, share photos, and stay in touch with friends. All in one fast and easy-to-use messaging app",
                        logo = Res.drawable.logo_cotam,
                        colorFirst = shuffledGradients[4].first,
                        colorSecond = shuffledGradients[4].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/cotam")
                        },
                        showGooglePlay = false
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.notefire_template,
                        title = "Notefire",
                        desc = "Note-taking app where you can take notes offline. You don't need an internet connection",
                        logo = Res.drawable.logo_notefire,
                        colorFirst = shuffledGradients[5].first,
                        colorSecond = shuffledGradients[5].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/notefirelocal")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.notefirelocal")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.notefire_cloud_template,
                        title = "Notefire Cloud",
                        desc = "Note anything online. Once you register, you can access your notes even with another phone.",
                        logo = Res.drawable.logo_notefire_cloud,
                        colorFirst = shuffledGradients[6].first,
                        colorSecond = shuffledGradients[6].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/notefirecloud")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.notefireremote")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.postgram_template,
                        title = "Postgram",
                        desc = "Postgram is a social media where you can post images and chat with your friends",
                        logo = Res.drawable.logo_postgram,
                        colorFirst = shuffledGradients[7].first,
                        colorSecond = shuffledGradients[7].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/postgram")
                        },
                        showGooglePlay = false
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.zaureats_template,
                        title = "ZaurEats",
                        desc = "Delivery app where you can order food from your favorite restaurant",
                        logo = Res.drawable.logo_zaureats,
                        colorFirst = shuffledGradients[8].first,
                        colorSecond = shuffledGradients[8].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/zaureats")
                        },
                        showGooglePlay = false
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.sunnyday_template,
                        title = "SunnyDay",
                        desc = "Weather app where you can search for a city and find out its daily weather data",
                        logo = Res.drawable.logo_sunnyday,
                        colorFirst = shuffledGradients[9].first,
                        colorSecond = shuffledGradients[9].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/sunnyday")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.sunnyday")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.durak_template,
                        title = "Durak",
                        desc = "Online card game where you can play with your friends or anyone",
                        logo = Res.drawable.logo_durak,
                        logoBackground = Color(0xFF6F6F6F),
                        colorFirst = shuffledGradients[10].first,
                        colorSecond = shuffledGradients[10].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/durak")
                        },
                        showGooglePlay = false
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.coinway_template,
                        title = "Coinway",
                        desc = "Crypto app where you can see crypto prices with a beautiful UI and line chart",
                        logo = Res.drawable.logo_coinway,
                        colorFirst = shuffledGradients[11].first,
                        colorSecond = shuffledGradients[11].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/coinway")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurh.coinway")
                        }
                    )
                    ProjectItem(
                        maxWidth = maxWidth,
                        template = Res.drawable.guessy_template,
                        title = "Guessy",
                        desc = "You enter your name, and the application will guess your gender",
                        logo = Res.drawable.logo_guessy,
                        colorFirst = shuffledGradients[12].first,
                        colorSecond = shuffledGradients[12].second,
                        onGithubClick = {
                            window.open("https://github.com/zaurh/guessy")
                        },
                        onGooglePlayClick = {
                            window.open("https://play.google.com/store/apps/details?id=com.zaurway.guessybyzaur")
                        }
                    )
                }

                Spacer(modifier = Modifier.height(36.dp))

                Box(
                    modifier = Modifier
                        .fillMaxWidth()
                        .onGloballyPositioned { coords ->
                            sectionOffsets["Contact"] = coords.positionInParent().y.roundToInt()
                        }
                ) {
                    ContactInfo(viewModel = viewModel)
                }

                Spacer(modifier = Modifier.height(36.dp))
            }
        }
    }
}